import React, { useEffect, useState } from 'react';

// Module-scoped singleton audio element so playback persists even if the UI unmounts
let sharedAudio = null;
let sharedAudioSrc = null;

function ensureAudio(src) {
  if (!sharedAudio) {
    sharedAudio = new Audio(src);
    sharedAudio.loop = true;
    sharedAudio.preload = 'auto';
    sharedAudio.muted = true;
    const stored = localStorage.getItem('audioVolume');
    sharedAudio.volume = stored ? Number(stored) : 0.3;
    sharedAudioSrc = src;
  } else if (src && src !== sharedAudioSrc) {
    sharedAudio.src = src;
    sharedAudioSrc = src;
  }
  return sharedAudio;
}

function AudioPlayer({ src = '/audio/arcade-theme.mp3' }) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(0.3); // 0.0 - 1.0

  useEffect(() => {
    const audio = ensureAudio(src);

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onVolume = () => {
      setVolume(audio.volume);
      setMuted(audio.muted);
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('volumechange', onVolume);

    // initialize state
    setPlaying(!audio.paused);
    setMuted(audio.muted);
    setVolume(audio.volume);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('volumechange', onVolume);
      // do not pause or destroy sharedAudio — we want it to persist
    };
  }, [src]);

  useEffect(() => {
    // persist volume to localStorage when changed
    localStorage.setItem('audioVolume', String(volume));
  }, [volume]);

  const togglePlay = async () => {
    const audio = ensureAudio(src);
    if (playing) {
      audio.pause();
    } else {
      try {
        await audio.play();
      } catch (err) {
        console.warn('Play prevented:', err);
      }
    }
  };

  const toggleMute = () => {
    const audio = ensureAudio(src);
    const nextMuted = !audio.muted;
    audio.muted = nextMuted;
    setMuted(nextMuted);

    // If unmuting and volume is 0, set a sensible default
    if (!nextMuted && audio.volume === 0) {
      audio.volume = 0.3;
      setVolume(0.3);
    }

    // Ensure playing when user interacts
    if (audio.paused && !nextMuted) {
      audio.play().catch(() => {});
    }
  };

  const onVolumeChange = (e) => {
    const val = Number(e.target.value) / 100; // slider is 0-100
    const audio = ensureAudio(src);
    audio.volume = val;
    audio.muted = val === 0;
    setVolume(val);
    setMuted(val === 0);
  };

  return (
    <div className="flex items-center gap-2">
      <button onClick={togglePlay} aria-pressed={playing} className="px-2 py-1 border border-neon neon-glow text-sm">
        {playing ? 'Pause' : 'Play'}
      </button>
      <button onClick={toggleMute} aria-pressed={!muted} className="px-2 py-1 border border-neon neon-glow text-sm">
        {muted ? 'Unmute' : 'Mute'}
      </button>

      <div className="flex items-center gap-2 ml-2">
        <input
          aria-label="Volume"
          type="range"
          min="0"
          max="100"
          value={Math.round(volume * 100)}
          onChange={onVolumeChange}
          className="w-24 range-neon"
        />
        <span className="text-xs">{Math.round(volume * 100)}%</span>
      </div>
    </div>
  );
}

export default AudioPlayer;
